import React, { useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import SubPageLayout from "../../../components/shared/SubPageLayout";
import api from "../../../utils/apiConfig";

import logger from '../../../utils/logger';

// Utility functions
const formatDate = (date) => {
  return new Date(date).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};

const formatTime = (date) => {
  return new Date(date).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

const Communications = () => {
  const { householdId } = useParams();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [household, setHousehold] = useState(null);
  const [selectedMembers, setSelectedMembers] = useState(new Set());
  const [communicationFeed, setCommunicationFeed] = useState([]);
  const [activeTab, setActiveTab] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [dateFilter, setDateFilter] = useState("all");
  const [showNewMessageModal, setShowNewMessageModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedCommunication, setSelectedCommunication] = useState(null);
  const [pinnedCommunications, setPinnedCommunications] = useState([]);
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const [actionItems, setActionItems] = useState([]);
  const [expandedThreads, setExpandedThreads] = useState(new Set());
  const [showPreferences, setShowPreferences] = useState(true);
  const [showMembers, setShowMembers] = useState(true);
  const [showQuickActions, setShowQuickActions] = useState(true);

  useEffect(() => {
    if (location.state) {
      const { activeTab: incomingTab, selectedMember } = location.state;
      if (incomingTab) setActiveTab(incomingTab);

      // Save the full member information, not just the ID
      if (selectedMember) {
        logger.info(
          "Received selected member from navigation:",
          selectedMember,
        );
        // Store the user_id (for backward compatibility) or userId
        const memberId = selectedMember.user_id || selectedMember.userId;
        setSelectedMembers(new Set([memberId]));

        // Store the full member object for later reference
        // This ensures we have the name even before the API call completes
        if (!household) {
          setHousehold((prev) => ({
            ...prev,
            members: [
              ...(prev?.members || []),
              // Only add if not already in the list
              ...(prev?.members?.some(
                (m) => m.userId === memberId || m.user_id === memberId,
              )
                ? []
                : [
                    {
                      userId: memberId,
                      user_id: memberId,
                      firstName:
                        selectedMember.firstName ||
                        selectedMember.Name?.first_name,
                      lastName:
                        selectedMember.lastName ||
                        selectedMember.Name?.last_name,
                      contact: selectedMember.contact || {},
                    },
                  ]),
            ],
          }));
        }
      }
    }
  }, [location.state, household]);

  useEffect(() => {
    const fetchHouseholdData = async () => {
      try {
        setLoading(true);

        // Corrected URL: Remove /client-database
        const url = `/advisor-portal/client-database/household-profile/${householdId}/communications`;
        logger.info("🔍 Attempting to fetch from:", url);

        const response = await api.get(url);

        logger.info("✅ Response received:", response.data);
        setHousehold(response.data);

        if (!location.state?.selectedMember) {
          setSelectedMembers(
            new Set(response.data.members.map((m) => m.userId)),
          );
        }

        if (
          response.data.communications &&
          response.data.communications.length > 0
        ) {
          setCommunicationFeed(response.data.communications);
        } else {
          const mockCommunications = response.data.members.flatMap((member) => [
            {
              id: `${member.userId}-1`,
              type: "email",
              subject: "Portfolio Review",
              preview: `Email to ${member.firstName} about portfolio review...`,
              date: new Date(2024, 2, 15, 14, 30),
              participants: [member.userId],
              status: "sent",
              attachments: 1,
              contact: member.contact,
            },
            {
              id: `${member.userId}-2`,
              type: "text",
              subject: null,
              preview: `Quick text to ${member.firstName} about upcoming meeting`,
              date: new Date(2024, 2, 14, 9, 15),
              participants: [member.userId],
              status: "sent",
              contact: member.contact,
            },
          ]);
          setCommunicationFeed(mockCommunications);
        }

        setLoading(false);
      } catch (err) {
        logger.error("❌ Error fetching household data:", err);
        logger.info("Error details:", {
          status: err.response?.status,
          data: err.response?.data,
          url: err.config?.url,
          headers: err.config?.headers,
        });
        setError(err.message || "Failed to fetch household data");
        setLoading(false);
      }
    };

    fetchHouseholdData();
  }, [householdId, location.state]);

  const handleMemberToggle = (memberId) => {
    const newSelected = new Set(selectedMembers);
    if (newSelected.has(memberId)) {
      newSelected.delete(memberId);
    } else {
      newSelected.add(memberId);
    }
    setSelectedMembers(newSelected);
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const handleDateFilterChange = (filter) => {
    setDateFilter(filter);
  };

  const filteredCommunications = communicationFeed.filter((comm) => {
    const memberMatch = comm.participants.some((p) => selectedMembers.has(p));
    const typeMatch = activeTab === "all" || comm.type === activeTab;
    const searchMatch =
      searchQuery === "" ||
      comm.subject?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      comm.preview.toLowerCase().includes(searchQuery.toLowerCase());

    let dateMatch = true;
    const now = new Date();
    const commDate = new Date(comm.date);

    switch (dateFilter) {
      case "today":
        dateMatch = commDate.toDateString() === now.toDateString();
        break;
      case "week":
        const weekAgo = new Date(now.setDate(now.getDate() - 7));
        dateMatch = commDate >= weekAgo;
        break;
      case "month":
        const monthAgo = new Date(now.setMonth(now.getMonth() - 1));
        dateMatch = commDate >= monthAgo;
        break;
      default:
        dateMatch = true;
    }

    return memberMatch && typeMatch && searchMatch && dateMatch;
  });

  const handlePinCommunication = (commId) => {
    setPinnedCommunications((prev) => {
      if (prev.includes(commId)) {
        return prev.filter((id) => id !== commId);
      }
      return [...prev, commId];
    });
  };

  const handleExpandThread = (threadId) => {
    setExpandedThreads((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(threadId)) {
        newSet.delete(threadId);
      } else {
        newSet.add(threadId);
      }
      return newSet;
    });
  };

  const toggleSection = (section) => {
    switch (section) {
      case "preferences":
        setShowPreferences((prev) => !prev);
        break;
      case "members":
        setShowMembers((prev) => !prev);
        break;
      case "quickActions":
        setShowQuickActions((prev) => !prev);
        break;
      default:
        break;
    }
  };

  const groupedCommunications = filteredCommunications.reduce(
    (groups, comm) => {
      const date = new Date(comm.date).toLocaleDateString();
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(comm);
      return groups;
    },
    {},
  );

  if (loading) {
    return (
      <SubPageLayout
        title="Loading..."
        breadcrumbItems={[
          { label: "Advisor Portal", path: "/advisor-portal" },
          { label: "Client Database", path: "/advisor-portal/client-database" },
          {
            label: "Household Profile",
            path: `/advisor-portal/client-database/household-profile/${householdId}`,
          },
          {
            label: "Communications",
            path: `/advisor-portal/client-database/household-profile/${householdId}/communications`,
          },
        ]}
      >
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </SubPageLayout>
    );
  }

  if (error) {
    return (
      <SubPageLayout
        title="Error"
        breadcrumbItems={[
          { label: "Advisor Portal", path: "/advisor-portal" },
          { label: "Client Database", path: "/advisor-portal/client-database" },
          {
            label: "Household Profile",
            path: `/advisor-portal/client-database/household-profile/${householdId}`,
          },
          {
            label: "Communications",
            path: `/advisor-portal/client-database/household-profile/${householdId}/communications`,
          },
        ]}
      >
        <div className="text-red-500">Error: {error}</div>
      </SubPageLayout>
    );
  }

  const breadcrumbItems = [
    { label: "Advisor Portal", path: "/advisor-portal" },
    { label: "Client Database", path: "/advisor-portal/client-database" },
    {
      label: "Household Profile",
      path: `/advisor-portal/client-database/household-profile/${householdId}`,
    },
    {
      label: "Communications",
      path: `/advisor-portal/client-database/household-profile/${householdId}/communications`,
    },
  ];

  return (
    <SubPageLayout
      title={`${household?.members[0]?.lastName || "Household"} Communications Hub`}
      subtitle="Manage and track all household communications"
      breadcrumbItems={breadcrumbItems}
    >
      <div className="flex">
        {/* Left Sidebar */}
        <div className="w-64 bg-gray-50 dark:bg-gray-800/50 border-r border-gray-200 dark:border-gray-700 flex flex-col">
          {/* Collapsible Household Members Section */}
          <div className="border-b border-gray-200 dark:border-gray-700">
            <button
              onClick={() => toggleSection("members")}
              className="w-full p-4 flex justify-between items-center hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              <h2 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Household Members
              </h2>
              <svg
                className={`w-5 h-5 text-gray-500 dark:text-gray-500 transform transition-transform ${showMembers ? "rotate-180" : ""}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
            {showMembers && (
              <div className="p-4 space-y-2">
                {household?.members.map((member) => (
                  <label
                    key={member.userId}
                    className="flex items-center space-x-2 cursor-pointer group"
                  >
                    <input
                      type="checkbox"
                      checked={selectedMembers.has(member.userId)}
                      onChange={() => handleMemberToggle(member.userId)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <div className="flex-1">
                      <span className="text-sm text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:text-gray-100">
                        {member.firstName} {member.lastName}
                      </span>
                      {member.isDependent && (
                        <span className="ml-2 px-2 py-0.5 text-xs bg-gray-100 text-gray-600 dark:text-gray-400 rounded">
                          Dependent
                        </span>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Collapsible Quick Actions Section */}
          <div className="border-b border-gray-200 dark:border-gray-700">
            <button
              onClick={() => toggleSection("quickActions")}
              className="w-full p-4 flex justify-between items-center hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              <h2 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Quick Actions
              </h2>
              <svg
                className={`w-5 h-5 text-gray-500 dark:text-gray-500 transform transition-transform ${showQuickActions ? "rotate-180" : ""}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
            {showQuickActions && (
              <div className="p-4 space-y-2">
                <button
                  onClick={() => setShowNewMessageModal(true)}
                  className="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:bg-blue-600 dark:hover:bg-blue-700"
                >
                  New Message
                </button>
                <button
                  onClick={() => setShowScheduleModal(true)}
                  className="w-full px-4 py-2 text-sm font-medium text-blue-700 bg-blue-100 rounded-md hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:bg-blue-900/30 dark:text-blue-300 dark:hover:bg-blue-900/50"
                >
                  Schedule Meeting
                </button>
              </div>
            )}
          </div>

          {/* Collapsible Communication Preferences Section */}
          <div className="border-b border-gray-200 dark:border-gray-700">
            <button
              onClick={() => toggleSection("preferences")}
              className="w-full p-4 flex justify-between items-center hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              <h2 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Communication Preferences
              </h2>
              <svg
                className={`w-5 h-5 text-gray-500 dark:text-gray-500 transform transition-transform ${showPreferences ? "rotate-180" : ""}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
            {showPreferences && (
              <div className="p-4 space-y-4">
                {household?.members.map((member) => (
                  <div key={member.userId} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {member.firstName} {member.lastName}
                      </span>
                      {member.isDependent && (
                        <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 dark:text-gray-400 rounded">
                          Limited Communication
                        </span>
                      )}
                    </div>
                    <div className="flex space-x-2">
                      <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded-full">
                        Email Preferred
                      </span>
                      <span className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded-full">
                        No calls after 8 PM
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Action Items Section */}
          <div className="p-4">
            <h2 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Action Items
            </h2>
            <div className="space-y-2">
              {actionItems.map((item, index) => (
                <div
                  key={index}
                  className="p-2 bg-yellow-50 border border-yellow-100 rounded-md text-sm"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-yellow-800">{item.text}</span>
                    <button
                      onClick={() => {
                        /* Handle complete */
                      }}
                      className="text-yellow-600 hover:text-yellow-800"
                    >
                      ✓
                    </button>
                  </div>
                  <div className="text-xs text-yellow-600 mt-1">
                    Due: {formatDate(item.dueDate)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col bg-gray-50 dark:bg-gray-800/30 overflow-hidden">
          {/* Enhanced Search and Filters */}
          <div className="p-4 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <div className="flex space-x-4">
                {/* Communication Type Tabs */}
                <div className="flex space-x-1">
                  {["all", "email", "text", "meeting"].map((tab) => (
                    <button
                      key={tab}
                      onClick={() => handleTabChange(tab)}
                      className={`px-3 py-2 text-sm font-medium rounded-md ${
                        activeTab === tab
                          ? "bg-blue-100 text-blue-700"
                          : "text-gray-500 dark:text-gray-500 hover:text-gray-700 dark:text-gray-300"
                      }`}
                    >
                      {tab.charAt(0).toUpperCase() + tab.slice(1)}
                    </button>
                  ))}
                </div>

                {/* Advanced Search Toggle */}
                <button
                  onClick={() => setShowAdvancedSearch((prev) => !prev)}
                  className="text-sm text-blue-600 hover:text-blue-700"
                >
                  Advanced Search
                </button>
              </div>

              {/* Search Box */}
              <div className="relative w-64">
                <input
                  type="text"
                  placeholder="Search communications..."
                  value={searchQuery}
                  onChange={handleSearch}
                  className="w-full rounded-md border-gray-300 text-sm focus:ring-blue-500 focus:border-blue-500 pl-8"
                />
                <svg
                  className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
            </div>

            {/* Advanced Search Panel */}
            {showAdvancedSearch && (
              <div className="mt-4 p-4 bg-gray-100 dark:bg-gray-800/50 rounded-md">
                <div className="grid grid-cols-3 gap-4">
                  {/* Date Range Picker */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Date Range
                    </label>
                    <select
                      value={dateFilter}
                      onChange={(e) => handleDateFilterChange(e.target.value)}
                      className="w-full rounded-md border-gray-300 dark:border-gray-600 text-sm bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                    >
                      <option value="all">All Time</option>
                      <option value="today">Today</option>
                      <option value="week">Past Week</option>
                      <option value="month">Past Month</option>
                      <option value="custom">Custom Range</option>
                    </select>
                  </div>

                  {/* Communication Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Type
                    </label>
                    <select
                      value={activeTab}
                      onChange={(e) => handleTabChange(e.target.value)}
                      className="w-full rounded-md border-gray-300 text-sm"
                    >
                      <option value="all">All Types</option>
                      <option value="email">Email</option>
                      <option value="text">Text</option>
                      <option value="meeting">Meeting</option>
                    </select>
                  </div>

                  {/* Status Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Status
                    </label>
                    <select className="w-full rounded-md border-gray-300 text-sm">
                      <option value="all">All Status</option>
                      <option value="unread">Unread</option>
                      <option value="flagged">Flagged</option>
                      <option value="completed">Completed</option>
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Pinned Communications */}
          {pinnedCommunications.length > 0 && (
            <div className="p-4 bg-yellow-50 border-b border-yellow-100">
              <h3 className="text-sm font-medium text-yellow-800 mb-2">
                Pinned
              </h3>
              <div className="space-y-2">
                {communicationFeed
                  .filter((comm) => pinnedCommunications.includes(comm.id))
                  .map((comm) => (
                    <div
                      key={comm.id}
                      className="bg-surface-default dark:bg-surface-default rounded-lg shadow-sm p-3 hover:shadow-md dark:shadow-none transition-shadow duration-200"
                    >
                      {/* Pinned communication card content */}
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Communication Feed */}
          <div className="flex-1 overflow-y-auto p-4">
            {Object.entries(groupedCommunications).map(
              ([date, communications]) => (
                <div key={date} className="mb-6">
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">
                    {date}
                  </h3>
                  <div className="space-y-4">
                    {communications.map((comm) => (
                      <div
                        key={comm.id}
                        className={`bg-gray-50 dark:bg-gray-800/50 rounded-lg shadow-sm hover:shadow-md dark:shadow-none transition-shadow duration-200 ${
                          selectedCommunication?.id === comm.id
                            ? "ring-2 ring-blue-500"
                            : ""
                        }`}
                      >
                        {/* Communication Card Header */}
                        <div
                          className="p-4 cursor-pointer"
                          onClick={() => setSelectedCommunication(comm)}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-center space-x-3">
                              {/* Type Icon */}
                              <div
                                className={`p-2 rounded-full ${
                                  comm.type === "email"
                                    ? "bg-blue-100 text-blue-600"
                                    : comm.type === "text"
                                      ? "bg-green-100 text-green-600"
                                      : "bg-purple-100 text-purple-600"
                                }`}
                              >
                                {comm.type === "email" && (
                                  <svg
                                    className="h-5 w-5"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                                    />
                                  </svg>
                                )}
                                {comm.type === "text" && (
                                  <svg
                                    className="h-5 w-5"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                                    />
                                  </svg>
                                )}
                                {comm.type === "meeting" && (
                                  <svg
                                    className="h-5 w-5"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                                    />
                                  </svg>
                                )}
                              </div>

                              {/* Communication Details */}
                              <div className="flex-1">
                                <div className="flex items-center space-x-2">
                                  <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                    {comm.subject || "No Subject"}
                                  </h3>
                                  {/* Participant Badges */}
                                  <div className="flex -space-x-1">
                                    {comm.participants.map((participantId) => {
                                      const member = household?.members.find(
                                        (m) => m.userId === participantId,
                                      );
                                      return member ? (
                                        <div
                                          key={participantId}
                                          className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-xs font-medium text-gray-600 dark:text-gray-400"
                                          title={`${member.firstName} ${member.lastName}`}
                                        >
                                          {member.firstName[0]}
                                          {member.lastName[0]}
                                        </div>
                                      ) : null;
                                    })}
                                  </div>
                                </div>
                                <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
                                  {comm.preview}
                                </p>
                              </div>
                            </div>

                            {/* Time and Actions */}
                            <div className="flex items-start space-x-4">
                              <div className="text-right">
                                <p className="text-sm text-gray-500 dark:text-gray-500">
                                  {formatTime(comm.date)}
                                </p>
                                {comm.attachments && (
                                  <div className="flex items-center text-xs text-gray-400 mt-1">
                                    <svg
                                      className="w-4 h-4 mr-1"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                                      />
                                    </svg>
                                    {comm.attachments}
                                  </div>
                                )}
                              </div>

                              {/* Quick Actions */}
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handlePinCommunication(comm.id);
                                  }}
                                  className={`p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 ${
                                    pinnedCommunications.includes(comm.id)
                                      ? "text-yellow-500"
                                      : "text-gray-400"
                                  }`}
                                >
                                  <svg
                                    className="w-4 h-4"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                  >
                                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                  </svg>
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Expanded Thread View */}
                        {expandedThreads.has(comm.id) && (
                          <div className="border-t border-gray-100 dark:border-gray-800 p-4 bg-gray-50">
                            {/* Thread content */}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ),
            )}
          </div>
        </div>

        {/* Right Panel - Communication Details */}
        {selectedCommunication && (
          <div className="w-96 bg-gray-50 dark:bg-gray-800/50 border-l border-gray-200 dark:border-gray-700 overflow-y-auto">
            <div className="sticky top-0 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700 p-4 z-10">
              <div className="flex justify-between items-start">
                <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">Details</h2>
                <button
                  onClick={() => setSelectedCommunication(null)}
                  className="text-gray-400 hover:text-gray-500 dark:text-gray-500"
                >
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>

            <div className="p-4 space-y-6">
              {/* Communication Metadata */}
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-500">Type</h3>
                  <p className="mt-1 text-sm text-gray-900 dark:text-gray-100 capitalize">
                    {selectedCommunication.type}
                  </p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-500">Subject</h3>
                  <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                    {selectedCommunication.subject || "No Subject"}
                  </p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-500">
                    Participants
                  </h3>
                  <div className="mt-1 flex flex-wrap gap-2">
                    {selectedCommunication.participants.map((participantId) => {
                      const member = household?.members.find(
                        (m) => m.userId === participantId,
                      );
                      return member ? (
                        <div
                          key={participantId}
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                        >
                          {member.firstName} {member.lastName}
                        </div>
                      ) : null;
                    })}
                  </div>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-500">
                    Date & Time
                  </h3>
                  <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                    {formatDate(selectedCommunication.date)} at{" "}
                    {formatTime(selectedCommunication.date)}
                  </p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-500">Status</h3>
                  <p className="mt-1 text-sm text-gray-900 dark:text-gray-100 capitalize">
                    {selectedCommunication.status}
                  </p>
                </div>
              </div>

              {/* Communication Content */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-500">Content</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap">
                    {selectedCommunication.preview}
                  </p>
                </div>
              </div>

              {/* Attachments */}
              {selectedCommunication.attachments && (
                <div className="space-y-4">
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-500">
                    Attachments
                  </h3>
                  <div className="space-y-2">
                    <div className="flex items-center p-2 bg-gray-50 rounded-lg">
                      <svg
                        className="w-5 h-5 text-gray-400 mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                        />
                      </svg>
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        Document.pdf
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Quick Reply */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-500">
                  Quick Reply
                </h3>
                <div className="space-y-2">
                  <textarea
                    placeholder="Type your reply..."
                    rows={3}
                    className="w-full rounded-lg border-gray-300 text-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                  <div className="flex justify-end space-x-2">
                    <button className="px-3 py-1 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:text-gray-200">
                      Cancel
                    </button>
                    <button className="px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700">
                      Send
                    </button>
                  </div>
                </div>
              </div>

              {/* AI Summary */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-500">
                  AI Summary
                </h3>
                <div className="bg-blue-50 rounded-lg p-4">
                  <p className="text-sm text-blue-900">
                    Key points from this communication:
                  </p>
                  <ul className="mt-2 space-y-1 text-sm text-blue-800">
                    <li>• Discussion about portfolio review</li>
                    <li>• Action item: Schedule follow-up meeting</li>
                    <li>• Documents needed: Latest statements</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </SubPageLayout>
  );
};

export default Communications;
